<template>
  <div v-if="showOverlay" class="access-suspension-overlay">
    <div class="access-suspension-panel" role="dialog" aria-modal="true">
      <div class="access-suspension-head">
        <div class="access-suspension-tile" :class="isOverdue ? 'is-overdue' : 'is-trial'">
          <svg v-if="isOverdue" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
            <path d="M12 17.5v-7M14 12h-2.75a1.25 1.25 0 0 0 0 2.5h1.5a1.25 1.25 0 0 1 0 2.5H10" />
          </svg>
          <svg v-else viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 22h14M5 2h14" />
            <path d="M17 22v-4.17a2 2 0 0 0-.59-1.42L12 12l-4.41 4.41A2 2 0 0 0 7 17.83V22" />
            <path d="M7 2v4.17a2 2 0 0 0 .59 1.42L12 12l4.41-4.41A2 2 0 0 0 17 6.17V2" />
          </svg>
        </div>
        <span class="access-suspension-badge" :class="isOverdue ? 'is-overdue' : 'is-trial'">
          {{ $t(`${i18nKey}.badge`) }}
        </span>
      </div>
      <h1 class="access-suspension-title">
        {{ $t(`${i18nKey}.title`) }}
      </h1>
      <p class="access-suspension-body">
        {{ $t(`${i18nKey}.body`) }}
      </p>

      <template v-if="isOverdue">
        <div class="access-suspension-reason">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
          <p>
            <strong>{{ $t("overduePaymentBlock.reasonLabel") }}</strong> <i18n
              v-if="dueDateLabel"
              path="overduePaymentBlock.reason"
              tag="span"
            >
              <template #days>{{ thresholdDays }}</template>
              <template #date><strong>{{ dueDateLabel }}</strong></template>
            </i18n><span v-else>{{
              $t("overduePaymentBlock.reasonNoDate", { days: thresholdDays })
            }}</span>
          </p>
        </div>
      </template>

      <div class="access-suspension-kept">
        <div class="access-suspension-kept-title">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="m9 12 2 2 4-4" />
          </svg>
          {{ $t("trialCommercialBlock.accountsKeptTitle") }}
        </div>
        <div class="access-suspension-kept-grid">
          <div class="access-suspension-kept-card">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <div>
              <strong>EdParent</strong>
              <span>{{ $t("trialCommercialBlock.parentsKept") }}</span>
            </div>
          </div>
          <div class="access-suspension-kept-card">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M22 10 12 5 2 10l10 5 10-5z" />
              <path d="M6 12v5c3 3 9 3 12 0v-5" />
            </svg>
            <div>
              <strong>EdTeacher</strong>
              <span>{{ $t("trialCommercialBlock.teachersKept") }}</span>
            </div>
          </div>
        </div>
        <p class="access-suspension-kept-note">
          {{ $t("trialCommercialBlock.accountsKeptFootnote") }}
        </p>
      </div>

      <p class="access-suspension-contact-title">
        {{ $t(`${i18nKey}.contactTitle`) }}
      </p>
      <div class="access-suspension-phones">
        <div
          v-for="phone in contacts.phones"
          :key="phone"
          class="access-suspension-contact"
        >
          <span class="access-suspension-contact-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />
            </svg>
          </span>
          <span class="access-suspension-contact-text">
            <small>{{ $t("trialCommercialBlock.phoneLabel") }}</small>
            <span>{{ phone }}</span>
          </span>
        </div>
      </div>
      <a class="access-suspension-contact is-email" :href="`mailto:${contacts.email}`">
        <span class="access-suspension-contact-icon">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
          </svg>
        </span>
        <span class="access-suspension-contact-text">
          <small>{{ $t("trialCommercialBlock.emailLabel") }}</small>
          <span>{{ contacts.email }}</span>
        </span>
        <svg class="access-suspension-chevron" viewBox="0 0 24 24" aria-hidden="true">
          <path d="m9 6 6 6-6 6" />
        </svg>
      </a>

      <div v-if="buildingOptions.length > 1" class="access-suspension-switch">
        <p class="access-suspension-switch-hint">
          {{ $t("trialCommercialBlock.switchBuildingHint") }}
        </p>
        <a-select
          v-model="selectedBuilding"
          class="access-suspension-select"
          option-filter-prop="children"
          show-search
          :getPopupContainer="getPopupContainer"
        >
          <a-select-option
            v-for="building in buildingOptions"
            :key="building.dbName"
            :value="building.dbName"
          >
            {{ building.name }}
          </a-select-option>
        </a-select>
        <a-button
          type="primary"
          :disabled="!selectedBuilding || selectedBuilding === settings.activeBuilding"
          @click="switchBuilding"
        >
          {{ $t("all.okay") }}
        </a-button>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import store from "store";
import {
  accessSuspension,
  ACCESS_SUSPENSION_TYPE,
  clearAccessSuspension,
} from "@/services/accessSuspension";
import { COMMERCIAL_CONTACTS } from "@/config/Constants";

export default {
  name: "AccessSuspensionOverlay",
  data() {
    return {
      selectedBuilding: "",
    };
  },
  computed: {
    accessSuspension() {
      return accessSuspension;
    },
    settings() {
      return this.$store.state.settings || {};
    },
    user() {
      return this.$store.state.user || {};
    },
    showOverlay() {
      if (!this.accessSuspension.active) return false;
      if (!this.accessSuspension.blockedDb) return true;
      return this.settings.activeBuilding === this.accessSuspension.blockedDb;
    },
    i18nKey() {
      return this.accessSuspension.type === ACCESS_SUSPENSION_TYPE.OVERDUE
        ? "overduePaymentBlock"
        : "trialCommercialBlock";
    },
    isOverdue() {
      return this.accessSuspension.type === ACCESS_SUSPENSION_TYPE.OVERDUE;
    },
    contacts() {
      return COMMERCIAL_CONTACTS;
    },
    overdueDetails() {
      const details = this.accessSuspension.details;
      return details && details.type === "OVERDUE_PAYMENT" ? details : null;
    },
    thresholdDays() {
      const days = this.overdueDetails && this.overdueDetails.thresholdDays;
      return Number.isInteger(days) ? days : 25;
    },
    dueDateLabel() {
      // dueDate is a Tunis calendar day (YYYY-MM-DD); shown as DD/MM/YYYY
      const dueDate = this.overdueDetails && this.overdueDetails.dueDate;
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dueDate || "");
      return match ? `${match[3]}/${match[2]}/${match[1]}` : "";
    },
    buildingOptions() {
      const buildings = this.user.building || [];
      const current = buildings.find((b) => b.dbName === this.settings.activeBuilding);
      const others = buildings.filter((b) => b.dbName !== this.settings.activeBuilding);
      return current ? [current, ...others] : others;
    },
  },
  watch: {
    buildingOptions: {
      immediate: true,
      handler(buildings) {
        if (!buildings.length) {
          this.selectedBuilding = "";
          return;
        }
        if (!this.selectedBuilding) {
          this.selectedBuilding = buildings[0].dbName;
        }
      },
    },
  },
  methods: {
    getPopupContainer(triggerNode) {
      return triggerNode.parentNode;
    },
    async switchBuilding() {
      const target = (this.user.building || []).find(
        (b) => b.dbName === this.selectedBuilding
      );
      if (!target) return;

      await store.set("DB", target.dbName);
      await this.$store.commit("settings/CHANGE_SETTING", {
        setting: "activeBuilding",
        value: target.dbName,
      });
      await this.$store.commit("settings/CHANGE_SETTING", {
        setting: "activeBuildingId",
        value: target._id,
      });

      const baseUrl = target.base_url || process.env.VUE_APP_API_URL;
      await this.$store.commit("settings/CHANGE_SETTING", {
        setting: "base_url",
        value: baseUrl,
      });
      apiClient.defaults.baseURL = `${baseUrl}/api/web`;

      clearAccessSuspension();
      window.location.reload();
    },
  },
};
</script>

<style scoped>
.access-suspension-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
  background: rgba(2, 6, 23, 0.65);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

.access-suspension-panel {
  box-sizing: border-box;
  width: 580px;
  max-width: 100%;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  border-radius: 20px;
  padding: 32px 36px;
  background: #ffffff;
  font-family: "Nunito Sans", "Mukta", sans-serif;
  color: #0f172a;
  box-shadow:
    0 30px 60px -25px rgba(0, 0, 0, 0.55),
    0 10px 24px -16px rgba(0, 0, 0, 0.35);
}

.access-suspension-panel svg {
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  flex-shrink: 0;
}

.access-suspension-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.access-suspension-tile {
  width: 52px;
  height: 52px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.access-suspension-tile svg {
  width: 26px;
  height: 26px;
}

.access-suspension-tile.is-trial {
  background: #eef2ff;
  color: #2547d0;
}

.access-suspension-tile.is-overdue {
  background: #fff4ec;
  color: #c2410c;
}

.access-suspension-badge {
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
}

.access-suspension-badge.is-trial {
  background: #fef3c7;
  color: #92400e;
}

.access-suspension-badge.is-overdue {
  background: #ffedd5;
  color: #c2410c;
}

.access-suspension-title {
  margin: 0 0 8px;
  font-family: inherit;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.25;
  color: #0f172a;
}

.access-suspension-body {
  margin: 0 0 22px;
  font-size: 15px;
  line-height: 1.7;
  color: #475569;
}

.access-suspension-reason {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #475569;
}

.access-suspension-reason svg {
  width: 18px;
  height: 18px;
}

.access-suspension-reason p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
}

.access-suspension-reason strong {
  color: #0f172a;
}

.access-suspension-kept {
  margin-bottom: 24px;
  padding: 18px 20px;
  border-radius: 12px;
  border: 1px solid #a7f3d0;
  background: #ecfdf5;
  color: #047857;
}

.access-suspension-kept-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  font-size: 15px;
  font-weight: 800;
}

.access-suspension-kept-title svg {
  width: 20px;
  height: 20px;
}

.access-suspension-kept-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.access-suspension-kept-card {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #ffffff;
}

.access-suspension-kept-card svg {
  width: 18px;
  height: 18px;
  margin-top: 2px;
}

.access-suspension-kept-card strong {
  display: block;
  font-size: 14px;
  color: #065f46;
}

.access-suspension-kept-card span {
  font-size: 13px;
  line-height: 1.5;
}

.access-suspension-kept-note {
  margin: 12px 0 0;
  font-size: 13px;
}

.access-suspension-contact-title {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
}

.access-suspension-phones {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.access-suspension-contact {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 12px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #0f172a;
}

.access-suspension-contact.is-email:hover {
  border-color: #2547d0;
  color: #0f172a;
}

.access-suspension-contact-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef2ff;
  color: #2547d0;
}

.access-suspension-contact-icon svg {
  width: 16px;
  height: 16px;
}

.access-suspension-contact-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1 1 auto;
}

.access-suspension-contact-text small {
  font-size: 11px;
  color: #475569;
}

.access-suspension-contact-text span {
  font-size: 16px;
  font-weight: 700;
  white-space: nowrap;
}

.access-suspension-chevron {
  width: 18px;
  height: 18px;
  color: #475569;
}

.access-suspension-switch {
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.access-suspension-switch-hint {
  width: 100%;
  margin: 0 0 2px;
  color: #334155;
  font-size: 0.92rem;
}

.access-suspension-select {
  flex: 1 1 auto;
}
</style>

